// src/pages/Estatisticas/components/AutomacoesStats.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

// ─── Mapeamentos de label e ícone por funcao ────────────────────────────────
const FUNCAO_LABELS = {
  honorarios_criados: 'Honorários Criados',
  checks_realizados:  'Conferência de Documentos',
  documentos_gerados: 'Documentos Gerados',
  law_cases_criados:  'Casos Analisados'
};

const FUNCAO_ICONS = {
  honorarios_criados: '💰',
  checks_realizados:  '✅',
  documentos_gerados: '📄',
  law_cases_criados:  '⚖️'
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatarMes = (anoMes) => {
  const [ano, mes] = anoMes.split('-');
  const date = new Date(parseInt(ano), parseInt(mes) - 1, 1);
  const label = date.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatarNumero = (numero) => Number(numero).toLocaleString('pt-BR');

// ─── Componente ─────────────────────────────────────────────────────────────
const AutomacoesStats = () => {
  const [data, setData]                     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedFuncoes, setExpandedFuncoes] = useState(new Set());

  useEffect(() => {
    carregarAutomacoes();
  }, []);

  const carregarAutomacoes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/estatisticas/automacoes');
      if (response.data.success) {
        setData(response.data.data);
        // Expande automaticamente o mês mais recente
        if (response.data.data.length > 0) {
          setExpandedMonths(new Set([response.data.data[0].ano_mes]));
        }
      } else {
        setError(response.data.message || 'Erro ao carregar automações');
      }
    } catch (err) {
      console.error('Error loading automacoes stats:', err);
      setError('Erro ao carregar automações: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleMonth = (anoMes) => {
    const next = new Set(expandedMonths);
    next.has(anoMes) ? next.delete(anoMes) : next.add(anoMes);
    setExpandedMonths(next);
  };

  const toggleFuncao = (key) => {
    const next = new Set(expandedFuncoes);
    next.has(key) ? next.delete(key) : next.add(key);
    setExpandedFuncoes(next);
  };

  // ── Estados de carregamento / erro / vazio ────────────────────────────────
  if (loading) {
    return (
      <div className="stat-box">
        <h3>🤖 Automações Realizadas</h3>
        <div className="loading-stats">
          <span>⏳</span>
          <span>Carregando automações...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stat-box">
        <h3>🤖 Automações Realizadas</h3>
        <div className="error-stats">{error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="stat-box">
        <h3>🤖 Automações Realizadas</h3>
        <div className="no-stats">
          <span>📊</span>
          <span>Nenhuma automação registrada no período</span>
        </div>
      </div>
    );
  }

  // ── Totalizador geral ─────────────────────────────────────────────────────
  const totalGeral = data.reduce(
    (sum, m) => sum + m.funcoes.reduce((s, f) => s + f.total_geral, 0),
    0
  );

  // ── Render principal ──────────────────────────────────────────────────────
  return (
    <div className="stat-box">
      <h3>🤖 Automações Realizadas</h3>

      {/* Resumo rápido */}
      <div className="access-summary-header">
        <div className="summary-item">
          <span className="summary-label">Total no Período:</span>
          <span className="summary-value">{formatarNumero(totalGeral)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Meses:</span>
          <span className="summary-value">{data.length}</span>
        </div>
      </div>

      {/* Lista colapsável mês → função → usuários */}
      <div className="access-functions-list">
        {data.map((monthData) => {
          const isMonthExpanded = expandedMonths.has(monthData.ano_mes);
          const totalMes = monthData.funcoes.reduce((s, f) => s + f.total_geral, 0);

          return (
            <div key={monthData.ano_mes} className="function-access-item">

              {/* ── Cabeçalho do mês ──────────────────────────────────── */}
              <div
                className="function-header"
                onClick={() => toggleMonth(monthData.ano_mes)}
              >
                <div className="function-info">
                  <span className="function-icon">📅</span>
                  <div className="function-details">
                    <span className="function-name">{formatarMes(monthData.ano_mes)}</span>
                    <span className="function-endpoint">{monthData.ano_mes}</span>
                  </div>
                </div>
                <div className="function-stats">
                  <span className="access-count">
                    {formatarNumero(totalMes)} automações
                  </span>
                  <span className="user-count">
                    {monthData.funcoes.length} função{monthData.funcoes.length !== 1 ? 'ões' : ''}
                  </span>
                  <span className={`expand-icon ${isMonthExpanded ? 'expanded' : ''}`}>▼</span>
                </div>
              </div>

              {/* ── Funções dentro do mês ─────────────────────────────── */}
              {isMonthExpanded && (
                <div style={{ paddingLeft: '16px' }}>
                  {monthData.funcoes.map((funcaoData) => {
                    const funcaoKey      = `${monthData.ano_mes}-${funcaoData.funcao}`;
                    const isFuncExpanded = expandedFuncoes.has(funcaoKey);
                    const label          = FUNCAO_LABELS[funcaoData.funcao] || funcaoData.funcao;
                    const icon           = FUNCAO_ICONS[funcaoData.funcao]  || '🔧';

                    return (
                      <div key={funcaoKey} className="function-access-item" style={{ marginBottom: '4px' }}>

                        {/* ── Cabeçalho da função ─────────────────────── */}
                        <div
                          className="function-header"
                          onClick={() => toggleFuncao(funcaoKey)}
                          style={{ background: 'rgba(255,255,255,0.04)' }}
                        >
                          <div className="function-info">
                            <span className="function-icon">{icon}</span>
                            <div className="function-details">
                              <span className="function-name">{label}</span>
                              <span className="function-endpoint">{funcaoData.funcao}</span>
                            </div>
                          </div>
                          <div className="function-stats">
                            <span className="access-count">
                              {formatarNumero(funcaoData.total_geral)} total
                            </span>
                            <span className="user-count">
                              {funcaoData.usuarios.length} usuário{funcaoData.usuarios.length !== 1 ? 's' : ''}
                            </span>
                            <span className={`expand-icon ${isFuncExpanded ? 'expanded' : ''}`}>▼</span>
                          </div>
                        </div>

                        {/* ── Tabela de usuários ──────────────────────── */}
                        {isFuncExpanded && (
                          <div className="users-table">
                            <div className="users-table-header">
                              <div className="user-cell">Usuário</div>
                              <div className="user-cell">Total</div>
                            </div>
                            {funcaoData.usuarios.map((user, idx) => (
                              <div key={idx} className="users-table-row">
                                <div className="user-cell user-email">{user.email}</div>
                                <div className="user-cell access-count-cell">
                                  {formatarNumero(user.total)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="stats-summary">
        <p>Clique em um mês ou função para ver o detalhe por usuário</p>
      </div>
    </div>
  );
};

export default AutomacoesStats;